/**
 * Workspace Theme — deep navy palette used exclusively for the internal
 * staff/manager UI (rendered inside <AppShell>). Public portal, ATS
 * recruiter, and Associate sandboxes are NOT affected.
 *
 * Locked constants (from UI/UX redesign handoff v2):
 *   - gold #D4AF37  (identity, never change)
 *   - navy #0B1724  (primary work surface)
 *   - deep black #0A0A0A (header/deep layers)
 */
export const WORKSPACE_DARK = {
  name: "dark",
  // ── Base surfaces ────────────────────────────────────────────────
  bg: "#0B1724",
  bgGradient: "linear-gradient(180deg, #0B1724 0%, #0A0F19 100%)",
  surface: "#101E2D",          // primary card / sheet
  surfaceAlt: "#0A1420",        // beneath a card
  surfaceCard: "#101E2D",       // NOTE: solid, not gradient (per spec)
  surfaceElevated: "#162536",  // modals / raised elements

  // ── Text ────────────────────────────────────────────────────────
  text: "#F5F7FA",
  textSoft: "#D5DCE5",
  textMuted: "#AAB4C0",
  textFaint: "#6B7684",

  // ── Borders (subtle, no more gold-everywhere) ────────────────────
  border: "rgba(255,255,255,0.10)",
  borderSoft: "rgba(255,255,255,0.06)",
  borderAccent: "rgba(212,175,55,0.35)",  // reserved for FOCUS state only
  borderStrong: "rgba(255,255,255,0.16)",

  // ── Inputs ──────────────────────────────────────────────────────
  inputBg: "#0A1420",
  inputBorder: "rgba(255,255,255,0.14)",

  // ── Gold (locked identity — used sparingly) ─────────────────────
  gold: "#D4AF37",
  goldDeep: "#B8902A",
  goldGradient: "linear-gradient(135deg, #D4AF37 0%, #B8902A 100%)",
  goldOnDark: "#D4AF37",

  // ── Semantic (single source of truth) ───────────────────────────
  success: "#16A34A",
  info: "#3B82F6",
  warning: "#F59E0B",
  danger: "#DC2626",

  errorBg: "rgba(220,38,38,0.12)",
  errorBorder: "rgba(220,38,38,0.35)",
  errorText: "#FCA5A5",

  // ── Chrome ──────────────────────────────────────────────────────
  headerBg: "rgba(10,15,25,0.85)",
  sidebarBg: "#0A1420",
  cardShadow: "0 8px 24px rgba(0,0,0,0.35)",
  sheetShadow: "0 20px 60px rgba(0,0,0,0.55)",
};

/**
 * Workspace Light theme — soft warm off-white with subtle navy tint.
 * Used only inside AppShell when the STAS/user toggles to light mode.
 */
export const WORKSPACE_LIGHT = {
  name: "light",
  bg: "#F5F5F1",
  bgGradient: "linear-gradient(180deg, #F7F6F1 0%, #ECEBE6 100%)",
  surface: "#FFFFFF",
  surfaceAlt: "#F0EEE8",
  surfaceCard: "#FFFFFF",
  surfaceElevated: "#F7F6F1",

  text: "#151821",
  textSoft: "#2A2E3A",
  textMuted: "#5A6270",
  textFaint: "#8A8F98",

  border: "rgba(0,0,0,0.12)",
  borderSoft: "rgba(0,0,0,0.07)",
  borderAccent: "rgba(184,144,42,0.55)",
  borderStrong: "rgba(0,0,0,0.18)",

  inputBg: "#FFFFFF",
  inputBorder: "rgba(0,0,0,0.15)",

  gold: "#A67C1A",
  goldDeep: "#8A6510",
  goldGradient: "linear-gradient(135deg, #C79A26 0%, #A67C1A 100%)",
  goldOnDark: "#A67C1A",

  success: "#16A34A",
  info: "#3B82F6",
  warning: "#D97706",
  danger: "#DC2626",

  errorBg: "rgba(220,38,38,0.08)",
  errorBorder: "rgba(220,38,38,0.45)",
  errorText: "#B91C1C",

  headerBg: "rgba(255,255,255,0.85)",
  sidebarBg: "#FFFFFF",
  cardShadow: "0 8px 24px rgba(40,30,10,0.10)",
  sheetShadow: "0 20px 50px rgba(40,30,10,0.15)",
};

export const WS_TOKENS = {
  breakpoints: { sm: 640, md: 768, lg: 1024, xl: 1280 },
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 8: 48 },
  radius: { sm: 8, md: 10, lg: 12, xl: 14, pill: 999 },
  motion: {
    fast: "150ms cubic-bezier(0.2,0.8,0.2,1)",
    base: "200ms cubic-bezier(0.2,0.8,0.2,1)",
    slow: "240ms cubic-bezier(0.2,0.8,0.2,1)",
  },
  touch: 44,
  sidebarWidth: 248,
  topBarHeight: 56,
  bottomNavHeight: 64,
};
